// Typed client for the /study-documents endpoints (standalone "Mark & Study a
// PDF" — server-persisted marked source PDFs, no generation).
//
// JSON calls go through the shared `api` client; the multipart upload uses raw
// fetch + a manual Bearer header the same way projects.ts / files.ts do (the
// browser must set the multipart boundary itself, so we can't route it through
// the JSON `api` client).
import { api, API_BASE_URL, API_PREFIX, ApiError } from "./client";
import { tokenStore } from "./token-store";

const enc = encodeURIComponent;

// A single mark is free-form (the server round-trips arbitrary fields, only
// special-casing `image_b64` → a stored `image_key` + minted `image_url`). The
// web keeps the marking workspace's camelCase local shape in each bucket.
export type StudyMark = Record<string, unknown>;

export interface StudyMarks {
  deep: StudyMark[];
  lookups: StudyMark[];
  highlights: StudyMark[];
}

export interface StudyDocumentSummary {
  id: string;
  filename: string;
  file_type: string;
  mark_count: number;
  created_at: string;
  updated_at: string;
  last_opened_at: string | null;
}

export interface StudyDocumentRead extends StudyDocumentSummary {
  storage_key: string;
  // Fresh presigned URL for the source PDF (minted on read; null if signing fails).
  pdf_url: string | null;
  extracted_text: string | null;
  // Marks with a fresh presigned `image_url` per stored `image_key`. null = unmarked.
  marks: StudyMarks | null;
  understanding: Record<string, unknown> | null;
}

// ── mark shape mapping (server ↔ workspace-local) ────────────────────────────
// Mirrors projects/project-marking's serverToLocal / localToServer bridge. The
// marking workspace spreads `{...mark}` on every edit, so it preserves fields it
// doesn't know about — we smuggle the server's stable `image_key` through as
// `imageKey` (and back). Snip bytes (`image_b64`) are only ever sent UP for a
// brand-new snip; the server never echoes them back (it returns an `image_url`
// instead), so a reseeded mark carries an empty `imageB64` and re-snips on edit.

/** Server mark → workspace-local mark (seed IndexedDB from the server). */
export function serverMarkToLocal(m: StudyMark): StudyMark {
  const out: StudyMark = { ...m };
  const key = out.image_key;
  delete out.image_key; // → smuggled through as `imageKey`
  delete out.image_url; // presigned + ephemeral; never persisted locally
  delete out.image_b64; // never present on a read, but be defensive
  out.imageKey = (key as string) ?? "";
  out.imageB64 = "";
  return out;
}

/** Workspace-local mark → server mark (mirror IndexedDB up to the server). */
export function localMarkToServer(m: StudyMark): StudyMark {
  const out: StudyMark = { ...m };
  const b64 = out.imageB64;
  const key = out.imageKey;
  delete out.imageB64;
  delete out.imageKey;
  // A stored snip keeps its stable key; a fresh snip ships its bytes (which the
  // server uploads and swaps for an `image_key`). Never send both.
  if (key) out.image_key = key;
  else if (b64) out.image_b64 = b64;
  return out;
}

// ── endpoints ────────────────────────────────────────────────────────────────

/** Multipart upload of a source PDF + its metadata. Returns the created doc. */
export async function uploadStudyDocument(opts: {
  file: File;
  filename: string;
  extractedText: string;
  understanding?: Record<string, unknown> | null;
}): Promise<StudyDocumentRead> {
  const form = new FormData();
  form.append("file", opts.file, opts.file.name);
  form.append("filename", opts.filename);
  form.append("extracted_text", opts.extractedText ?? "");
  if (opts.understanding) form.append("understanding", JSON.stringify(opts.understanding));

  const headers: Record<string, string> = { Accept: "application/json" };
  const token = tokenStore.access;
  if (token) headers.Authorization = `Bearer ${token}`;
  // Do NOT set Content-Type — the browser sets the multipart boundary.

  const res = await fetch(`${API_BASE_URL}${API_PREFIX}/study-documents`, {
    method: "POST",
    headers,
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const data = await res.json();
      if (typeof data?.detail === "string") message = data.detail;
    } catch {
      /* keep generic */
    }
    throw new ApiError(res.status, message);
  }
  return (await res.json()) as StudyDocumentRead;
}

export const listStudyDocuments = () =>
  api.get<StudyDocumentSummary[]>("/study-documents");

export const getStudyDocument = (id: string) =>
  api.get<StudyDocumentRead>(`/study-documents/${enc(id)}`);

export const saveStudyMarks = (id: string, marks: StudyMarks) =>
  api.patch<StudyDocumentRead>(`/study-documents/${enc(id)}/marks`, { marks });

export const updateStudyDocument = (
  id: string,
  patch: { filename?: string; understanding?: Record<string, unknown> | null },
) => api.patch<StudyDocumentRead>(`/study-documents/${enc(id)}`, patch);

export const deleteStudyDocument = (id: string) =>
  api.delete<void>(`/study-documents/${enc(id)}`);
